import { Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';

import {
  accessTokenReceived,
  loginFailure,
  loginSuccess,
} from './authentication.actions';
import { authenticationQuery } from './authentication.selectors';
import { storageFactory } from '../constants';

@Injectable()
export class AuthenticationFacade {
  hasValidAccessToken$: Observable<boolean> = this.store.pipe(
    select(authenticationQuery.getHasAccessToken),
  );

  getExpirationDate$: Observable<Date> = this.store.pipe(
    select(authenticationQuery.getExpirationDate),
  );

  get events() {
    return this.oAuthService.events;
  }

  get returnUrl() {
    return decodeURIComponent(this.oAuthService.state) || '/';
  }

  constructor(protected store: Store, private oAuthService: OAuthService) {}

  clearReturnUrl() {
    this.oAuthService.state = '';
  }

  clearStorage() {
    (storageFactory() as Storage).clear();
  }

  accessTokenReceived() {
    const expiresAt = this.oAuthService.getAccessTokenExpiration();
    this.store.dispatch(accessTokenReceived({ expiresAt }));
  }

  hasValidAccessToken() {
    return this.oAuthService.hasValidAccessToken();
  }

  configure(config: AuthConfig) {
    this.oAuthService.configure(config);
  }

  setupAutomaticSilentRefresh(
    params?: object,
    listenTo?: 'any' | 'access_token' | 'id_token',
    noPrompt?: boolean,
  ) {
    this.oAuthService.setupAutomaticSilentRefresh(params, listenTo, noPrompt);
  }

  initLoginFlow(returnUrl: string, params?: {}) {
    this.oAuthService.initCodeFlow(returnUrl, params);
  }

  tryLogin() {
    this.oAuthService
      .tryLogin()
      .then((result) => {
        if (result) this.dispatchLoginSuccess();
      })
      .catch((error) => this.dispatchLoginFailure(error));
  }

  logOut() {
    this.oAuthService.logOut();
  }

  private dispatchLoginSuccess() {
    this.store.dispatch(loginSuccess());
  }

  private dispatchLoginFailure(error: unknown) {
    this.store.dispatch(loginFailure({ error }));
  }
}
